import React, { useState } from 'react'
import { Button, Dropdown, Empty, Flex, List, Popconfirm, Space, Spin } from 'antd'
import { DeleteOutlined, DownOutlined, PlusOutlined, ToolOutlined } from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import PatternIcon from '../../../../../../components/common/PatternIcon'
import Text from 'antd/es/typography/Text'
import Title from 'antd/es/typography/Title'
import { 
    useFetchRubricsQuery,
    useAssignRubricToQuestionMutation,
    useRemoveRubricFromQuestionMutation,
} from '../../../../../../store/apis/rubric.api'
import { openNotificationWithIcon } from '../../../../../../utils/notification.helper'
import { useAssignmentAccess } from '../../../../components/access/Access'
import { Perm, State } from '../../../../../../constants/assignment-permissions'
import QuestionRubricToolsDrawer from '../QuestionRubricToolsDrawer'

const RubricsTab = ({ question }) => {
    const { token } = useParams()
    const { data: allRubrics, isLoading: isLoadingRubrics } = useFetchRubricsQuery({ token })
    const [assignRubric, assignResult] = useAssignRubricToQuestionMutation()
    const [removeRubric, removeResult] = useRemoveRubricFromQuestionMutation()
    const [selectedRubricForTools, setSelectedRubricForTools] = useState(null)

    const { hasAccess: hasEditPermission } = useAssignmentAccess([
        Perm.CAN_EDIT, 
        State.NOT_PROCESSING, 
        State.NOT_MBA, 
        State.UNPUBLISHED, 
        State.LATEST,
        State.NO_SUBMISSIONS,
    ])

    const questionRubrics = question?.questionRubrics || []
    const assignedRubricIds = questionRubrics.map(r => r.rubricId)
    
    // Get full rubric data (with categories) for assigned rubrics
    const rubrics = questionRubrics.map(qr => {
        const fullRubric = allRubrics?.find(r => r.id === qr.rubricId)
        return {
            ...qr,
            name: fullRubric?.name || qr.name,
            categories: fullRubric?.categories || [],
        }
    })

    // Available rubrics to assign (not already assigned)
    const availableRubrics = allRubrics?.filter(r => !assignedRubricIds.includes(r.id)) || []

    const handleAssignRubric = async (rubricId) => {
        const response = await assignRubric({
            questionId: question.id,
            rubricId,
            position: questionRubrics.length,
        })
        if ('data' in response) {
            openNotificationWithIcon('success', 'Rubric assigned to question')
        } else {
            openNotificationWithIcon('error', 'Failed to assign rubric')
        }
    }

    const handleRemoveRubric = async (questionRubricId) => {
        const response = await removeRubric({ questionRubricId })
        if ('data' in response) {
            openNotificationWithIcon('success', 'Rubric removed from question')
        } else {
            openNotificationWithIcon('error', 'Failed to remove rubric')
        }
    }

    const handleOpenToolsDrawer = (rubric) => {
        setSelectedRubricForTools(rubric)
    }

    const handleCloseToolsDrawer = () => {
        setSelectedRubricForTools(null)
    }

    const rubricMenuItems = availableRubrics.map(rubric => ({
        key: rubric.id,
        label: (
            <Space>
                <span>{rubric.name}</span>
                <Text type='secondary'>({rubric.categories?.length || 0} categories)</Text>
            </Space>
        ),
        onClick: () => handleAssignRubric(rubric.id),
    }))

    const header = (
        <Flex justify='space-between' align='center' style={{ marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0 }}>
                Rubrics
            </Title>
            {hasEditPermission && availableRubrics.length > 0 && (
                <Dropdown
                    menu={{ 
                        items: rubricMenuItems,
                        style: { maxHeight: 480, overflowY: 'auto' }
                    }}
                    trigger={['click']}
                    placement='bottomRight'
                    disabled={assignResult.isLoading}
                >
                    <Button
                        icon={<PlusOutlined aria-hidden='true' />}
                        loading={assignResult.isLoading}
                        type='text'
                    >
                        Assign <DownOutlined />
                    </Button>
                </Dropdown>
            )}
        </Flex>
    )

    if (isLoadingRubrics) {
        return (
            <Flex vertical>
                {header}
                <Spin tip='Loading...' />
            </Flex>
        )
    }

    if (!rubrics.length) {
        return (
            <Flex vertical>
                {header}
                <Empty 
                    description='No rubrics assigned to this question yet.'
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Flex>
        )
    }

    return (
        <Flex vertical>
            {header}
            <List
                dataSource={rubrics}
                renderItem={rubric => (
                    <List.Item
                        actions={[
                            <Button
                                key='tools'
                                type='text'
                                icon={<ToolOutlined />}
                                onClick={() => handleOpenToolsDrawer(rubric)}
                                title='Tools'
                            />,
                            ...(hasEditPermission ? [
                                <Popconfirm
                                    key='remove'
                                    title='Remove Rubric'
                                    description='Are you sure you want to remove this rubric from the question?'
                                    onConfirm={() => handleRemoveRubric(rubric.id)}
                                    okText='Remove'
                                    okButtonProps={{ danger: true }}
                                    cancelText='Cancel'
                                >
                                    <Button
                                        type='text'
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={removeResult.isLoading}
                                        title='Remove rubric from question'
                                    />
                                </Popconfirm>
                            ] : [])
                        ]}
                    >
                        <List.Item.Meta
                            title={rubric.name}
                            description={
                                rubric.categories?.length > 0 ? (
                                    <Flex vertical gap={4} style={{ marginTop: 8 }}>
                                        {rubric.categories.map(category => (
                                            <Flex key={category.id} align='center' gap={8}>
                                                <PatternIcon
                                                    id={category.id}
                                                    colorId={category.colorId}
                                                    content={category.grade}
                                                    suffix='pt'
                                                />
                                                <Text>{category.name}</Text>
                                            </Flex>
                                        ))}
                                    </Flex>
                                ) : (
                                    <Text type='secondary' italic>No categories in this rubric</Text>
                                )
                            }
                        />
                    </List.Item>
                )}
            />
            <QuestionRubricToolsDrawer 
                questionRubric={selectedRubricForTools} 
                onClose={handleCloseToolsDrawer} 
            />
        </Flex>
    )
}

export default RubricsTab
